// DashboardV2 design tokens — single source of truth for the app shell + dashboard.
// Modelled on Asana's semantic token system: every surface, border, text and icon
// value is a named role, never a raw hex in a component. Two full themes resolved
// at runtime by `useDashTheme`.

// ── Color system ──────────────────────────────────────────────────────────
export const palette = {
  light: {
    /* Surfaces (low → high elevation)
       ────────────────────────────────────────────────────────────────────
       Retuned 2026-08-11. The previous values had two faults that together
       made every light-mode page read as one flat white sheet:
         · the canvas was #F2F3F4 against a #FFFFFF card — a 3% lightness
           step, below the point where an edge reads as an edge, so cards
           did not appear to sit on anything;
         · surfaceMuted was #F2F3F4, IDENTICAL to the canvas, so every inset
           well, track and hover state was literally invisible.
       The canvas is now meaningfully deeper and slightly cool, and the three
       levels (canvas / well / card) are distinct from each other. Hues are
       unchanged — this is a contrast fix, not a re-brand. Dark mode is
       untouched; it already had real separation. */
    bg: "#D4DBE6",           // app canvas — matches the graphical dashboard paper() canvas (2026-08-29); was #E9ECF1
    bgGradient: "none",      // Asana has no atmospheric wash
    surface: "#FFFFFF",      // cards (background-base)
    surfaceGlass: "rgba(255,255,255,0.85)",
    surfaceMuted: "#F1F3F7", // inset wells — now distinct from BOTH bg and surface
    surfaceHover: "#E3E7EE", // background-strong
    // Borders
    border: "#DCE0E7",
    borderStrong: "#BFC5CF",
    // Text
    text: "#1D1F21",
    textSecondary: "#626364",
    /* Measured 2026-08-12: the previous #A9ABAD sat at 2.30:1 on a white card
       — far under the 4.5:1 needed for body text, and it was being used for
       real content (timestamps, captions, evidence lines), not just chrome.
       #65686D clears 4.5:1 on all three surfaces: card 5.59, canvas 4.72,
       inset 5.04. Same neutral hue, readable weight. */
    textTertiary: "#65686D",
    textInverse: "#FFFFFF",
    // Icon
    icon: "#757677",
    iconHover: "#1D1F21",
    // Primary action blue + selected state
    primary: "#4273D1",
    primaryHover: "#335FB5",
    primarySoft: "#E9F1FE",
    primaryRing: "rgba(66, 115, 209, 0.28)",
    primaryGradient: "#4273D1", // kept as a token; Asana fills are flat
    // Brand coral — accent only (create affordance, AI glow). Never a large fill.
    brand: "#FF584A",
    // Intelligence violet — marks surfaces the model writes into (ask bar,
    // generated signals) so AI output is recognisable without a gradient.
    ai: "#6D4AFF",
    aiSoft: "rgba(109, 74, 255, 0.10)",
    aiBorder: "rgba(109, 74, 255, 0.45)",
    aiGlow: "rgba(109, 74, 255, 0.38)",
    // Shadows
    shadowColor: "rgba(24, 32, 46, 0.09)",
    shadowColorStrong: "rgba(24, 32, 46, 0.16)",
    glow: "rgba(255, 88, 74, 0.22)",
  },
  dark: {
    bg: "#1D1F21",
    bgGradient: "none",
    surface: "#252728",
    surfaceGlass: "rgba(37, 39, 40, 0.85)",
    surfaceMuted: "#2A2C2E",
    surfaceHover: "#303234",
    border: "#404244",
    borderStrong: "#4F5052",
    text: "#F2F3F4",
    textSecondary: "#9EA0A2",
    // Was #626364 → 2.49:1 on the dark card. #96989A gives 5.18:1.
    textTertiary: "#96989A",
    textInverse: "#1D1F21",
    icon: "#9EA0A2",
    iconHover: "#F2F3F4",
    primary: "#789FE8",
    primaryHover: "#9BB8EF",
    primarySoft: "#131F33",
    primaryRing: "rgba(120, 159, 232, 0.34)",
    primaryGradient: "#789FE8",
    brand: "#FF584A",
    ai: "#A88BFF",
    aiSoft: "rgba(168, 139, 255, 0.12)",
    aiBorder: "rgba(168, 139, 255, 0.55)",
    aiGlow: "rgba(168, 139, 255, 0.5)",
    shadowColor: "rgba(29, 31, 33, 0.30)",
    shadowColorStrong: "rgba(29, 31, 33, 0.50)",
    glow: "rgba(255, 88, 74, 0.28)",
  },
};

// Semantic status colors — meaning must not shift with theme.
export const status = {
  positive: "#50AB5B",
  positiveSoft: "rgba(80, 171, 91, 0.14)",
  negative: "#E5484D",
  negativeSoft: "rgba(229, 72, 77, 0.13)",
  warning: "#A29B4E",
  warningSoft: "rgba(162, 155, 78, 0.16)",
  info: "#4273D1",
  infoSoft: "rgba(66, 115, 209, 0.12)",
  neutral: "#757677",
  neutralSoft: "rgba(117, 118, 119, 0.12)",
};

// ── Named accent families ────────────────────────────────────────────────
// Each family pairs a pastel background with a dark same-hue foreground so
// glyphs and text stay legible on the tint. Powers avatars, project/template
// tiles and tag pills.
export const accentPairs = {
  red: { bg: "#FC8589", fg: "#5D0214" },
  orange: { bg: "#F59E96", fg: "#5B0B0E" },
  yellow: { bg: "#EECC16", fg: "#322B0A" },
  yellowGreen: { bg: "#C1E064", fg: "#272E10" },
  green: { bg: "#79DC48", fg: "#1E3017" },
  blueGreen: { bg: "#70D791", fg: "#0E321B" },
  aqua: { bg: "#82E1F7", fg: "#0F3037" },
  blue: { bg: "#72ABF2", fg: "#132C4B" },
  indigo: { bg: "#ABADF7", fg: "#2B167A" },
  purple: { bg: "#C5A6FE", fg: "#34244E" },
  magenta: { bg: "#E8B2F7", fg: "#441850" },
  hotPink: { bg: "#FB96C2", fg: "#550C34" },
  pink: { bg: "#FCA7E4", fg: "#4B103F" },
  coolGray: { bg: "#96ACA7", fg: "#1E2C29" },
};

export const accentNames = Object.keys(accentPairs);

// Deterministic family pick — same input always maps to the same color.
export const accentFor = (seed = "") => {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) % 9973;
  return accentPairs[accentNames[h % accentNames.length]];
};

// Chart spread — the saturated foregrounds read well on white at small sizes.
//
// ⚠ Measured 2026-08-17: this spread does NOT clear colour-vision-deficiency
// separation. Adjacent slots 6/7 (aqua #2F8F9D ↔ magenta #B4589E) sit at
// ΔE 0.3 under deuteranopia — a deuteranope cannot tell them apart at all —
// and green/yellow fall under 3:1 on a white card. It is left as-is because
// existing panels depend on these exact hexes; new chart work should use
// `series` below. Fixing this spread in place is its own change.
export const accents = [
  "#4273D1", // blue
  "#50AB5B", // green
  "#756BC4", // purple
  "#A29B4E", // yellow
  "#FF584A", // coral
  "#2F8F9D", // aqua
  "#B4589E", // magenta
  "#757677", // gray
];

/* ── Categorical series (validated) ───────────────────────────────────────
   The spread for anything that encodes IDENTITY — a project, a tool, a
   segment. Two themes because a single set cannot clear the contrast floor
   on both a white card and a #252728 one; these are the same seven hues
   stepped for each ground, so slot N is the same *entity colour* in both.

   Checked with the dataviz validator against this system's real surfaces:
     dark  vs #252728 — every gate passes (worst adjacent CVD ΔE 13.0,
                        worst adjacent normal-vision ΔE 16.7, all ≥ 3:1)
     light vs #FFFFFF — separation gates pass (CVD 16.3, normal 18.4); teal,
                        yellow and pink land under 3:1, so any panel using
                        them MUST carry a visible label beside the mark. Every
                        Pulse panel direct-labels its series, which is the
                        documented relief for that warning.
   Order is the CVD-safety mechanism, not decoration — re-run the validator
   before reordering or substituting a hue. */
export const series = {
  light: ["#00A89B", "#2a78d6", "#D97706", "#e87ba4", "#008300", "#4a3aa7", "#eb6834"],
  dark: ["#00A89B", "#3987e5", "#c98500", "#d55181", "#008300", "#9085e9", "#d95926"],
};

/* Idle is never a series hue — it is the absence of work, so it stays a
   chosen neutral biased toward the accent family. Both steps clear 3:1 on
   their own ground and clear the ΔE 15 floor against series slot 1, which
   they sit directly on top of in every stacked column. */
export const idleNeutral = { light: "#556275", dark: "#5B6A80" };

/* Sequential ramp for the week heatmap: one hue, light→dark, monotonic in
   lightness in both themes (step 0 is "nothing tracked", not a low value). */
export const heatRamp = {
  light: ["#EDF0F6", "#CCECE7", "#95DBD2", "#55C3B7", "#10A699", "#00847A"],
  dark: ["#20242B", "#123B3D", "#075858", "#00756F", "#00A093", "#12D0BE"],
};

// ── Spacing / radius / type ───────────────────────────────────────────────
export const space = { 0: 0, 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 7: 32, 8: 40, 9: 48, 10: 64 };

/* How wide the page content is allowed to run before it stops and centres.
   Raised from 1380 to 1680 on 2026-08-27: measured on a 2300px viewport, the
   old cap left 323px of bare canvas on EACH side — a third of the usable
   width — and #D4DBE6 is saturated enough that the dead space read as a
   fault rather than as margin. Removing the cap entirely was worse: stat
   cards and progress bars stretch thin and the eye loses the row. 1680 keeps
   the content bounded while reclaiming ~300px.
   Both the dashboard and every drill read this, so they cannot drift apart. */
export const layout = { contentMax: 1680 };

// Asana uses a small repeated set, not a long scale:
// 4 tiny pills/inputs · 6 buttons/tabs · 8 cards/nav/tiles · 16 fully-rounded pills.
//
// `card` / `hero` are the graphical dashboard's own card language, which runs
// larger than the shell scale above (16–24 vs 4–8). Added 2026-08-27: those
// surfaces were hardcoding 13/14/16/18/20/24 inline, so the same metric tile
// rendered at 24px on the dashboard and 14px one click into a drill-down.
// Two roles, named, so a tile reads the same at any depth. The xs–pill steps
// are untouched — every other screen keeps its current radii.
// `well` is a surface nested *inside* a card — it steps down from `card` so the
// nesting reads, which is why it is a role of its own and not drift.
export const radius = { xs: 4, sm: 6, md: 8, lg: 8, xl: 8, pill: 999, well: 14, card: 16, hero: 24 };

export const font = {
  family: '-apple-system, system-ui, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif',
  numeric: '-apple-system, system-ui, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  size: {
    xs: 11,       // micro labels
    sm: 12,       // meta / table headers / captions
    base: 14,     // body — the workhorse
    md: 13,       // dense body (toolbars, dense rows)
    lg: 15,       // in-page section headings
    xl: 18,
    "2xl": 20,    // KPI figures
    "3xl": 24,    // page title
    display: 24,
  },
  // Only regular / medium / semibold. No light, no heavy.
  weight: { regular: 400, medium: 500, semibold: 600, bold: 600 },

  /* The two uppercase label roles for the graphical dashboard. Added
     2026-08-27: that surface had grown seven near-identical specs
     (11/800/1.4, 11/800/0.4, 10/800/0.8, 10.5/800/0.7, 12/700/1.1,
     9/700/0.5, 10.5/700/0.735) — close enough to read as "the same thing"
     and so invisible by eye, obvious in the numbers. 0.735px and 10.5px were
     computed leftovers, not chosen values.

     `statLabel` names a metric — the hero tile and every drill-down tile.
     Colour is deliberately absent: it is the theme's primary, resolved by the
     caller, so the label follows the theme rather than pinning a hex here.
     `microLabel` is the smaller sub-role: table column heads, chip captions. */
  statLabel: { fontSize: 11, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase" },
  microLabel: { fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" },
};

// Elevation is deliberately subtle: a 1px hairline ring plus a soft drop.
export const elevation = (t) => ({
  none: "none",
  sm: `0 1px 3px ${t.shadowColor}`,
  md: `0 4px 12px ${t.shadowColor}`,
  lg: `0 8px 24px ${t.shadowColorStrong}`,
  card: `0 0 0 1px ${t.border}, 0 1px 3px ${t.shadowColor}`,
  /* The graphical dashboard's two-layer lift: a tight contact shadow plus a
     wide, heavily-negative-spread ambient one. Was duplicated as a literal in
     HeroBand, KpiStrip and DrillHero; named here 2026-08-27 so the hero tile
     and the drill tiles cannot drift apart again. `raisedHover` is the same
     shape with the ambient layer opened up. */
  raised: `0 1px 1px ${t.shadowColor}, 0 18px 40px -24px ${t.shadowColorStrong}`,
  raisedHover: `0 1px 1px ${t.shadowColor}, 0 28px 50px -20px ${t.shadowColorStrong}`,
});

// ── Motion ────────────────────────────────────────────────────────────────
export const motion = {
  spring: {
    soft: { type: "spring", stiffness: 260, damping: 30, mass: 0.9 },
    snappy: { type: "spring", stiffness: 480, damping: 34, mass: 0.7 },
    gentle: { type: "spring", stiffness: 180, damping: 26, mass: 1 },
  },
  ease: {
    out: [0.16, 1, 0.3, 1],
    inOut: [0.65, 0, 0.35, 1],
  },
  dur: { fast: 0.18, base: 0.28, slow: 0.45 },
};

// Stagger container/item for grid reveals.
export const stagger = {
  container: { animate: { transition: { staggerChildren: 0.05, delayChildren: 0.04 } } },
  item: {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: motion.ease.out } },
  },
};
